import { useHistory } from "react-router-dom";
import { useBranding } from "../../Components/branding/Branding";
import { getValidLoggedinUser } from "../../helpers/authToken";

/**
 * Playful catch-all 404 page. Rendered by the catch-all route in Routes/index.js
 * whenever a URL matches neither the public nor the auth route lists (e.g.
 * /sdfghfd). It lives OUTSIDE the main layouts, so it is fully self-contained.
 */
const NotFound = () => {
  const history = useHistory();
  const { isBrandingActive } = useBranding();
  const isLoggedIn = !!getValidLoggedinUser();
  const appName = isBrandingActive ? "CRMit" : "the app";

  const goHome = () => {
    history.push(isLoggedIn ? "/dashboard" : "/login");
  };

  return (
    <div className="not-found-page">
      <style>{`
        .not-found-page {
          min-height: calc(100vh - 140px);
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 48px 24px;
          border-radius: 16px;
          background: radial-gradient(1200px 600px at 50% -10%, rgba(64,81,181,0.18), transparent 60%),
                      linear-gradient(160deg, #0f1230 0%, #1b1f4b 45%, #2a2f6b 100%);
          color: #eef0ff;
          font-family: inherit;
          overflow: hidden;
        }
        .not-found-card {
          position: relative;
          max-width: 560px;
          width: 100%;
          text-align: center;
          z-index: 2;
        }
        .nf-illustration { width: 220px; height: 220px; margin: 0 auto 8px; display: block; }
        .nf-astronaut { animation: nf-float 6s ease-in-out infinite; transform-origin: center; }
        @keyframes nf-float {
          0%, 100% { transform: translateY(0) rotate(-4deg); }
          50%      { transform: translateY(-14px) rotate(4deg); }
        }
        .nf-code {
          font-size: 84px;
          font-weight: 800;
          letter-spacing: 4px;
          line-height: 1;
          margin: 0;
          background: linear-gradient(90deg, #7c86ff, #b39dff);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .nf-title { font-size: 24px; font-weight: 700; margin: 10px 0 6px; }
        .nf-text { font-size: 15px; opacity: 0.8; margin: 0 auto 26px; max-width: 420px; }
        .nf-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          border: none;
          cursor: pointer;
          padding: 12px 26px;
          border-radius: 999px;
          font-size: 15px;
          font-weight: 600;
          color: #fff;
          background: linear-gradient(90deg, #5b6cff, #8a5bff);
          box-shadow: 0 10px 30px rgba(91,108,255,0.45);
          transition: transform .15s ease, box-shadow .15s ease;
        }
        .nf-btn:hover { transform: translateY(-2px); box-shadow: 0 14px 36px rgba(91,108,255,0.55); }
        .nf-star { position: absolute; border-radius: 50%; background: #fff; opacity: 0.7; animation: nf-twinkle 3s ease-in-out infinite; }
        @keyframes nf-twinkle { 0%,100% { opacity: 0.15; } 50% { opacity: 0.9; } }
      `}</style>

      {/* twinkly stars */}
      <span className="nf-star" style={{ top: "12%", left: "18%", width: 4, height: 4, animationDelay: "0s" }} />
      <span className="nf-star" style={{ top: "22%", left: "78%", width: 3, height: 3, animationDelay: "0.6s" }} />
      <span className="nf-star" style={{ top: "68%", left: "12%", width: 5, height: 5, animationDelay: "1.1s" }} />
      <span className="nf-star" style={{ top: "78%", left: "82%", width: 3, height: 3, animationDelay: "1.7s" }} />
      <span className="nf-star" style={{ top: "40%", left: "8%", width: 3, height: 3, animationDelay: "2.2s" }} />
      <span className="nf-star" style={{ top: "35%", left: "90%", width: 4, height: 4, animationDelay: "2.8s" }} />

      <div className="not-found-card">
        <svg className="nf-illustration" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <circle cx="100" cy="100" r="92" fill="rgba(255,255,255,0.03)" />
          <circle cx="100" cy="100" r="70" fill="rgba(255,255,255,0.03)" />
          <g className="nf-astronaut">
            {/* tether */}
            <path d="M60 150 Q40 120 55 95" stroke="#8a93ff" strokeWidth="2" strokeLinecap="round" strokeDasharray="4 5" />
            {/* backpack */}
            <rect x="72" y="78" width="56" height="60" rx="18" fill="#c9cff2" />
            {/* body */}
            <rect x="78" y="70" width="44" height="66" rx="20" fill="#ffffff" />
            {/* arms */}
            <rect x="60" y="86" width="26" height="15" rx="7.5" fill="#ffffff" transform="rotate(-18 60 86)" />
            <rect x="118" y="82" width="26" height="15" rx="7.5" fill="#ffffff" transform="rotate(20 118 82)" />
            {/* legs */}
            <rect x="84" y="128" width="15" height="30" rx="7.5" fill="#ffffff" transform="rotate(10 84 128)" />
            <rect x="104" y="128" width="15" height="30" rx="7.5" fill="#ffffff" transform="rotate(-10 104 128)" />
            {/* helmet */}
            <circle cx="100" cy="66" r="26" fill="#ffffff" />
            <circle cx="100" cy="66" r="19" fill="#20244d" />
            <ellipse cx="93" cy="60" rx="6" ry="8" fill="#3a4080" opacity="0.9" />
            {/* little confused eyes on visor reflection */}
            <circle cx="104" cy="66" r="2.4" fill="#aab0ff" />
            <circle cx="112" cy="70" r="1.6" fill="#aab0ff" opacity="0.7" />
          </g>
        </svg>

        <p className="nf-code">404</p>
        <h1 className="nf-title">Well, this is awkward… 🚀</h1>
        <p className="nf-text">
          Not sure how you landed all the way out here. This page doesn’t exist
          (or drifted off into space). Let’s get you back to somewhere useful in{" "}
          {appName}.
        </p>
        <button type="button" className="nf-btn" onClick={goHome}>
          {isLoggedIn ? "Beam me back to the dashboard" : "Take me to login"}
        </button>
      </div>
    </div>
  );
};

export default NotFound;
